import { AddressSchema, formatAddress, states } from '@fastre/core/schemas/generic'
import { Box, Link, Option, Select, Stack } from '@mui/joy'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { useEditMode } from 'listings/editMode'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import { AutocompleteProps, SlotAutocomplete } from './autocomplete'
import Input, { SlotInput, SlotWrapper } from './input'
import { useShowSnack } from './snackbar'
import { MobileFriendlyStack } from './stack'

interface LocationOption {
    description: string
    placeId: string
}

interface LocationAutocompleteProps
    extends Omit<
        AutocompleteProps<LocationOption, false, false, false>,
        'error' | 'options' | 'value' | 'onChange'
    > {
    name: string
    control: any
    disabled?: boolean
    allowManual?: boolean
    geoName?: string
}

const LocationAutocomplete = (props: LocationAutocompleteProps) => {
    const placesLib = useMapsLibrary('places')
    const autoCompleteService = useMemo(() => placesLib && new placesLib.AutocompleteService(), [placesLib])
    const placesService = useMemo(
        () => placesLib && new placesLib.PlacesService(document.createElement('div')),
        [placesLib],
    )
    const showSnack = useShowSnack()
    const formContext = useFormContext()
    const { editMode } = useEditMode()

    const [internalVal, setInternalVal] = useState<string | null>(null)
    const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[] | null>(null)
    const [loading, setLoading] = useState(false)

    const isManual = useWatch({ control: props.control, name: `${props.name}.manual` }) ?? false

    const [debouncedInternalVal] = useDebounce(internalVal, 200)

    const getSuggestions = (input: string): Promise<google.maps.places.AutocompletePrediction[]> =>
        new Promise((resolve, reject) => {
            autoCompleteService!.getPlacePredictions(
                {
                    input,
                    types: ['address'],
                    componentRestrictions: {
                        country: 'au',
                    },
                },
                (predictions, status) => {
                    if (status === 'OK') {
                        resolve(predictions ?? [])
                    } else {
                        resolve([])
                    }
                },
            )
        })

    useEffect(() => {
        if (debouncedInternalVal) {
            if (autoCompleteService) {
                getSuggestions(debouncedInternalVal).then(setSuggestions)
            }
        } else {
            setSuggestions([])
        }
    }, [debouncedInternalVal])

    const getPlaceDetails = async (placeId: string): Promise<google.maps.places.PlaceResult | null> => {
        return new Promise((resolve, reject) => {
            if (!placesService) return reject(new Error('Google Maps library not loaded'))

            placesService.getDetails(
                { placeId, fields: ['address_components', 'formatted_address', 'geometry'] },
                (place, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                        resolve(place)
                    } else {
                        reject(status)
                    }
                },
            )
        })
    }

    const handleToggleMode = (e: React.MouseEvent) => {
        e.preventDefault()
        if (!isManual) {
            formContext.setValue(props.name, { manual: true })
        } else {
            formContext.setValue(props.name, undefined)
        }
    }

    if (!formContext) return null

    // If we're in a form context, use Controller
    return (
        <Controller
            name={props.name}
            control={props.control}
            disabled={props.disabled}
            render={({ field }) => {
                // Update internal state when form value changes
                useEffect(() => {
                    if (field.value) {
                        setInternalVal(formatAddress(field.value, 'full'))
                    }
                }, [field.value])

                if (!editMode) {
                    return (
                        <Input
                            label="Address"
                            value={field.value ? formatAddress(field.value, 'full') : ''}
                            disabled={props.disabled}
                            required={props.required}
                        />
                    )
                }

                return (
                    <Stack spacing={1}>
                        {!isManual ? (
                            <SlotAutocomplete
                                {...props}
                                field={field}
                                formState={formContext.formState}
                                options={
                                    suggestions?.map(({ description, place_id }) => ({
                                        description,
                                        placeId: place_id,
                                    })) ?? []
                                }
                                onInputChange={(e: any) => {
                                    if (e) {
                                        setInternalVal(e.target.value)
                                    }
                                }}
                                onBlur={() =>
                                    setInternalVal(field.value ? formatAddress(field.value, 'full') : null)
                                }
                                getOptionLabel={(option: any) => {
                                    if (typeof option === 'string') return option
                                    if (option.streetNumber) return formatAddress(option as any, 'full')
                                    return option.description
                                }}
                                isOptionEqualToValue={(option: LocationOption, value: LocationOption) =>
                                    option.description.replace(', Australia', '') === value.description
                                }
                                onChange={async (e, v) => {
                                    if (isManual) return

                                    console.log('v', v)
                                    const placeId = v?.placeId ?? null

                                    if (placeId && placesService) {
                                        var details = await getPlaceDetails(placeId)
                                        var getGeoProp = (prop: string) =>
                                            details?.address_components?.find(c => c.types.includes(prop))
                                                ?.short_name ?? undefined

                                        if (getGeoProp('street_number') == undefined) {
                                            const suggestWithUnitWord = (
                                                await getSuggestions('unit ' + debouncedInternalVal)
                                            )[0]
                                            if (suggestWithUnitWord) {
                                                const details = await getPlaceDetails(
                                                    suggestWithUnitWord.place_id,
                                                )
                                                console.log('new details', details)
                                                getGeoProp = (prop: string) =>
                                                    details?.address_components?.find(c =>
                                                        c.types.includes(prop),
                                                    )?.short_name ?? undefined
                                            } else {
                                                showSnack('Please enter a valid address', 'danger')
                                                return
                                            }
                                        }

                                        console.log('details', details)

                                        if (details) {
                                            const newAddress: AddressSchema = {
                                                unitNumber: getGeoProp('subpremise')!,
                                                streetNumber: getGeoProp('street_number')!,
                                                streetName: getGeoProp('route')!,
                                                suburb: getGeoProp('locality')!,
                                                state: getGeoProp('administrative_area_level_1') as any,
                                                postcode: getGeoProp('postal_code')!,
                                                manual: false,
                                            }
                                            console.log('newAddress', newAddress)
                                            field.onChange(newAddress)

                                            // Update geoLocation if geoName is provided and geometry is available
                                            if (props.geoName) {
                                                if (details.geometry?.location) {
                                                    const geoLocation = [
                                                        details.geometry.location.lat(),
                                                        details.geometry.location.lng(),
                                                    ]
                                                    formContext.setValue(props.geoName, geoLocation)
                                                } else {
                                                    // Unset geoLocation if no valid geometry is available
                                                    formContext.setValue(props.geoName, undefined)
                                                }
                                            }
                                        }
                                    }
                                }}
                                loading={loading}
                            />
                        ) : (
                            <>
                                <MobileFriendlyStack>
                                    <Controller
                                        name={`${props.name}.unitNumber`}
                                        control={props.control}
                                        render={field => (
                                            <SlotInput
                                                label="Unit Number (optional)"
                                                {...field}
                                            />
                                        )}
                                    />
                                </MobileFriendlyStack>
                                <MobileFriendlyStack>
                                    <Controller
                                        name={`${props.name}.streetNumber`}
                                        control={props.control}
                                        render={field => (
                                            <SlotInput
                                                label="Street Number"
                                                {...field}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`${props.name}.streetName`}
                                        control={props.control}
                                        render={field => (
                                            <SlotInput
                                                label="Street Name"
                                                {...field}
                                                required
                                            />
                                        )}
                                    />
                                </MobileFriendlyStack>
                                <MobileFriendlyStack>
                                    <Controller
                                        name={`${props.name}.suburb`}
                                        control={props.control}
                                        render={field => (
                                            <SlotInput
                                                label="Suburb"
                                                {...field}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`${props.name}.state`}
                                        control={props.control}
                                        render={field => (
                                            <SlotWrapper
                                                {...field}
                                                label="State"
                                            >
                                                <Select onChange={(e, value) => field.field.onChange(value)}>
                                                    {states.map(state => (
                                                        <Option
                                                            key={state}
                                                            value={state}
                                                        >
                                                            {state}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </SlotWrapper>
                                        )}
                                    />
                                </MobileFriendlyStack>
                                <MobileFriendlyStack>
                                    <Controller
                                        name={`${props.name}.postcode`}
                                        control={props.control}
                                        render={field => (
                                            <SlotInput
                                                label="Postcode"
                                                {...field}
                                            />
                                        )}
                                    />
                                </MobileFriendlyStack>
                            </>
                        )}
                        {props.allowManual && editMode && (
                            <Box sx={{ pb: 2 }}>
                                <Link
                                    component="button"
                                    onClick={handleToggleMode}
                                    sx={{ alignSelf: 'flex-start' }}
                                >
                                    {isManual ? 'Use Address Autocomplete' : 'Enter Address Manually'}
                                </Link>
                            </Box>
                        )}
                    </Stack>
                )
            }}
        />
    )
}

export default LocationAutocomplete
